// src/components/AddProject.jsx
import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { MaterialReactTable } from "material-react-table";
import { Link, useNavigate, useLocation } from "react-router-dom";
import Barcode from "react-barcode";
import {
  Printer,
  Edit,
  Trash2,
  Copy,
  PlusCircle,
  MinusCircle,
  X,
  Check,
} from "lucide-react";
import {
  Box,
  Card,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
} from "@mui/material";
import { tokens } from "../../theme";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { getToken, base_url } from "../../config/config";
import QualityNoteCard from "./QualityNote";

// -----------------------------
// ANR Validation Functions
// -----------------------------
const isParentProject = (anr) => {
  // Regex pattern to match projects ending with -00-00-YY (YY being the year)
  const pattern = /^\d+-00-00-\d+$/;
  return pattern.test(anr);
};

const isSubProject = (anr) => {
  // Check if it's a valid ANR format but not a parent project
  const pattern = /^\d+-\d+-\d+-\d+$/;
  return pattern.test(anr) && !isParentProject(anr);
};

const getParentANRFromSub = (subANR) => {
  if (!isSubProject(subANR)) return null;

  const parts = subANR.split("-");
  if (parts.length !== 4) return null;

  return `${parts[0]}-00-00-${parts[3]}`;
};

const checkParentExists = async (parentANR, token) => {
  try {
    const response = await fetch(`${base_url}/v1/getProjectbyANR`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ ANR: parentANR }),
    });

    const result = await response.json();
    return result.status === 1 && result.data.length > 0;
  } catch (error) {
    console.error("Error checking parent ANR:", error);
    return false;
  }
};

const createParentANR = async (parentANR, baseProject = null, token) => {
  try {
    const parentProjectData = new FormData();
    parentProjectData.append("ANR", parentANR);
    parentProjectData.append(
      "Auftragsbezeichnung",
      `Hauptprojekt ${parentANR.split("-")[0]}`,
    );
    parentProjectData.append("status", "Geplant");
    parentProjectData.append(
      "deadline",
      new Date().toISOString().split("T")[0],
    );
    parentProjectData.append(
      "completionDate",
      new Date().toISOString().split("T")[0],
    );
    parentProjectData.append("Menge", "1");
    parentProjectData.append("MengeCost", "0");
    parentProjectData.append("MaxLohnaufwand", "0");
    parentProjectData.append("maxlohnfundUnit", "hours");
    parentProjectData.append("percentDone", "0");
    parentProjectData.append("invoiceNo", "");
    parentProjectData.append("totalMaterialCost", "0");
    parentProjectData.append("totalsubcontratorCost", "0");
    parentProjectData.append("Auftragserlös", "0");
    parentProjectData.append("auftragswertNetto", "0");
    parentProjectData.append("shippingMethod", "");
    parentProjectData.append("packagingType", "");
    parentProjectData.append("Artikelnummer", "");
    parentProjectData.append("Baugruppenzeichnung", "");
    parentProjectData.append("Erlauterung", "");
    parentProjectData.append("singlemengecost", "0");

    // Copy some basic info from base project if provided
    if (baseProject) {
      if (baseProject.kundenID) {
        parentProjectData.append("kundenID", baseProject.kundenID);
      }
      if (baseProject.kostenstelle) {
        parentProjectData.append("kostenstelle", baseProject.kostenstelle);
      }
    }

    const response = await fetch(`${base_url}/v1/createProject`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: parentProjectData,
    });

    const result = await response.json();
    if (result.status === 1) {
      toast.success(`Hauptprojekt ${parentANR} automatisch erstellt`, {
        autoClose: 3000,
      });
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error creating parent ANR:", error);
    toast.error(`Fehler beim Erstellen des Hauptprojekts ${parentANR}`, {
      autoClose: 3000,
    });
    return false;
  }
};

const validateANRForSave = async (anr, formData, token) => {
  if (!anr)
    return {
      valid: true,
      stats: { parentCreated: 0, subCreated: 0, createdANRs: [] },
    };

  const stats = {
    parentCreated: 0,
    subCreated: 0,
    createdANRs: [],
  };

  // If it's a parent project, track it
  if (isParentProject(anr)) {
    stats.subCreated = 0; // This will be the main project being created
    return { valid: true, stats };
  }

  // If it's a sub-project, check if parent exists
  if (isSubProject(anr)) {
    const parentANR = getParentANRFromSub(anr);
    if (!parentANR) {
      toast.error("Ungültiges ANR-Format", { autoClose: 3000 });
      return { valid: false, stats };
    }

    const parentExists = await checkParentExists(parentANR, token);
    if (!parentExists) {
      // Ask user if they want to create the parent automatically
      const shouldCreateParent = window.confirm(
        `Das Hauptprojekt ${parentANR} existiert nicht. Soll es automatisch erstellt werden?`,
      );

      if (shouldCreateParent) {
        const created = await createParentANR(parentANR, formData, token);
        if (!created) {
          return { valid: false, stats };
        }
        // Track the parent creation
        stats.parentCreated = 1;
        stats.createdANRs.push({
          anr: parentANR,
          type: "parent",
          timestamp: new Date(),
        });
      } else {
        toast.error(
          `Hauptprojekt ${parentANR} muss existieren, bevor Unterprojekte erstellt werden können`,
          { autoClose: 3000 },
        );
        return { valid: false, stats };
      }
    }

    // Track the sub-project being created
    stats.subCreated = 1;
    stats.createdANRs.push({ anr, type: "sub", timestamp: new Date() });
  }

  return { valid: true, stats };
};

// -----------------------------
// Updated InputField Component with a more rectangular & larger styling
// -----------------------------
const InputField = React.memo(
  ({ label, name, value, onChange, type = "text", placeholder, ...rest }) => (
    <div>
      {label && <label className="block text-sm font-medium">{label}</label>}
      <input
        type={type}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        {...rest}
        className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base text-black focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
      />
    </div>
  ),
);

// -----------------------------
// Memoized ProjectForm Component
// Note: This component now also receives maxLohnaufwandUnit and setMaxLohnaufwandUnit props
// -----------------------------
const ProjectForm = React.memo(
  ({
    formData,
    setFormData,
    handleSubmit,
    selectedKunde,
    setShowKundeModal,
    maxLohnaufwandUnit,
    setMaxLohnaufwandUnit,
    shippingMethod,
    setShippingMethod,
    packagingType,
    setPackagingType,
    externalService,
    setExternalService,
    handleQualityDataChange,
    editMode = false, // Add this prop to indicate if we're editing
    initialExternalService = [], // Add this prop for initial data
    highestANRForYear, // Add this prop
    checkForDuplicateANR, // Add this prop
    setShowDuplicateModal, // Add this prop
    setDuplicateProject, // Add this prop
    duplicateProject, // Add this prop
    debounceTimer, // Add this prop
    setDebounceTimer, // Add this prop
    handleDescriptionChange, // Add this prop
    descriptionSuggestions = [], // Add this prop
    showDescriptionSuggestions = false, // Add this prop
    handleSelectSuggestion, // Add this prop
    setShowDescriptionSuggestions, // Add this prop
  }) => {
    const handleInputChange = useCallback(
      (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
      },
      [setFormData],
    );
    const normalizeExternalService = (value) => {
      if (Array.isArray(value)) {
        return value;
      } else if (typeof value === "string" && value.trim()) {
        try {
          // Try to parse as JSON first (in case it's a stringified array)
          const parsed = JSON.parse(value);
          return Array.isArray(parsed) ? parsed : [value];
        } catch (e) {
          // If parsing fails, treat as single string value
          return [value];
        }
      }
      return [];
    };

    // Initialize external service when in edit mode
    useEffect(() => {
      if (editMode && initialExternalService) {
        const normalized = normalizeExternalService(initialExternalService);
        setExternalService(normalized);
      }
    }, [editMode, initialExternalService, setExternalService]);

    // Cleanup suggestion timer on unmount
    useEffect(() => {
      return () => {
        // Clear timer on component unmount
      };
    }, []);

    return (
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Field 1: Kalkulationsmittelllohn [KML] €/Std */}
          <div>
            <label className="block text-sm font-medium pb-4">
              Kalkulationsmittelllohn [KML] €/Std
            </label>
            <InputField
              name="MengeCost"
              value={formData.MengeCost}
              onChange={handleInputChange}
              placeholder="z.B. 60"
            />
          </div>
          {/* Field 2: ANR */}
          <div>
            <label className="block text-sm font-medium">ANR</label>
            <p className="text-xs">(XXXX-XX-XX-XX)</p>

            {/* ANR Format Help */}
            <div className="text-xs mb-2 p-2 bg-green-50 border border-green-200 rounded">
              <p className="text-green-700 font-semibold">
                📋 ANR Format Regeln:
              </p>
              <p className="text-green-600">
                <span className="inline-block w-2 h-2 bg-blue-500 rounded mr-1"></span>
                <strong>Hauptprojekt:</strong> XXXX-00-00-YY (z.B.
                0341-00-00-25)
              </p>
              <p className="text-green-600">
                <span className="inline-block w-2 h-2 bg-purple-500 rounded mr-1"></span>
                <strong>Unterprojekt:</strong> XXXX-XX-XX-YY (z.B.
                0341-01-02-25)
              </p>
              <p className="text-green-600 text-xs">
                ℹ️ Hauptprojekte werden automatisch erstellt falls sie fehlen
              </p>
            </div>

            {highestANRForYear && (
              <p className="text-xs text-blue-600 mb-2">
                Höchste ANR für {new Date().getFullYear()}: {highestANRForYear}
              </p>
            )}

            <div className="relative">
              <InputField
                name="ANR"
                value={formData.ANR}
                onChange={(e) => {
                  const input = e.target.value;
                  if (/^[0-9-]*$/.test(input)) {
                    handleInputChange(e);

                    // Clear existing debounce timer
                    if (debounceTimer) {
                      clearTimeout(debounceTimer);
                    }

                    // Check for duplicates when user finishes typing ANR in correct format (debounced)
                    if (input.match(/^\d{4}-\d{2}-\d{2}-\d{2}$/)) {
                      const timer = setTimeout(async () => {
                        const existingProject =
                          await checkForDuplicateANR(input);
                        if (existingProject) {
                          setShowDuplicateModal(true);
                        }
                      }, 500); // 500ms delay
                      setDebounceTimer(timer);
                    } else {
                      // Clear duplicate project if ANR format is incomplete
                      setDuplicateProject(null);
                    }
                  }
                }}
              />

              {/* ANR Type Indicator */}
              {formData.ANR &&
                formData.ANR.match(/^\d{4}-\d{2}-\d{2}-\d{2}$/) && (
                  <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                    {isParentProject(formData.ANR) ? (
                      <span className="inline-block px-2 py-1 text-xs font-bold text-blue-700 bg-blue-100 border border-blue-300 rounded">
                        HAUPT
                      </span>
                    ) : isSubProject(formData.ANR) ? (
                      <span className="inline-block px-2 py-1 text-xs font-bold text-purple-700 bg-purple-100 border border-purple-300 rounded">
                        SUB
                      </span>
                    ) : (
                      <span className="inline-block px-2 py-1 text-xs font-bold text-gray-700 bg-gray-100 border border-gray-300 rounded">
                        NORMAL
                      </span>
                    )}
                  </div>
                )}
            </div>

            {duplicateProject && (
              <p className="text-xs text-red-600 mt-1">
                ⚠️ Diese ANR existiert bereits!
              </p>
            )}
          </div>
          {/* Field 3: Werkstückbezeichnung with autocomplete */}
          <div className="relative">
            <label className="block text-sm font-medium">
              Werkstückbezeichnung
            </label>
            <input
              type="text"
              name="form_auftragsbezeichnung"
              value={formData.form_auftragsbezeichnung}
              onChange={handleDescriptionChange}
              onBlur={() =>
                setTimeout(() => setShowDescriptionSuggestions(false), 200)
              }
              onFocus={() => {
                if (
                  formData.form_auftragsbezeichnung &&
                  descriptionSuggestions.length > 0
                ) {
                  setShowDescriptionSuggestions(true);
                }
              }}
              placeholder="Geben Sie eine Beschreibung ein..."
              className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base text-black focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
            />
            {showDescriptionSuggestions &&
              descriptionSuggestions.length > 0 && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-y-auto">
                  {descriptionSuggestions.map((suggestion, index) => (
                    <div
                      key={index}
                      onClick={() => handleSelectSuggestion(suggestion)}
                      className="px-4 py-2 cursor-pointer hover:bg-blue-100 border-b border-gray-200 last:border-b-0 text-black text-sm"
                    >
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
          </div>
          {/* Field 4: Menge */}
          <InputField
            label="Menge"
            name="Menge"
            value={formData.Menge}
            onChange={handleInputChange}
          />
          {/* Field 5: Kunde selection */}
          <div>
            <label className="block text-sm font-medium ">Kunde</label>
            <button
              type="button"
              onClick={() => setShowKundeModal(true)}
              className="mt-1 block w-full border border-gray-300 px-4 py-3 text-black text-base text-left bg-white transition duration-150 ease-in-out"
            >
              {selectedKunde
                ? `${selectedKunde.konto} - ${selectedKunde.bezeichnung}`
                : "Kunde auswählen"}
            </button>
          </div>
          {/* Field 6: Auftragserteilungsdatum */}
          <InputField
            label="Auftragserteilungsdatum"
            name="deadline"
            type="date"
            value={formData.deadline || ""}
            onChange={handleInputChange}
          />
          {/* Field 7: Fertigstellungstermin */}
          <InputField
            label="Fertigstellungstermin"
            name="completionDate"
            type="date"
            value={formData.completionDate || ""}
            onChange={handleInputChange}
          />
          {/* Field 8: Kostenstelle */}
          <InputField
            label="Kostenstelle"
            name="kostenstelle"
            value={formData.kostenstelle || ""}
            onChange={handleInputChange}
          />
          <InputField
            label="Lieferdatum"
            name="Lieferdatum"
            type="date"
            value={formData.Lieferdatum || ""}
            onChange={handleInputChange}
          />
          <InputField
            label="Auftragserlös"
            name="Auftragserlös"
            value={formData.Auftragserlös || ""}
            onChange={handleInputChange}
          />
          {/* Field 7: Artikelnummer */}
          <InputField
            label="Artikelnummer"
            name="Artikelnummer"
            value={formData.Artikelnummer}
            onChange={handleInputChange}
          />
          {/* Field 8: Zeichnungs-Nr */}
          <InputField
            label="Zeichnungs-Nr"
            name="form_baugruppenzeichnung"
            value={formData.form_baugruppenzeichnung}
            onChange={handleInputChange}
          />
          {/* Field 9: Einzelmengenkosten */}
          <InputField
            label="Einzelmengenkosten"
            name="singlemengecost"
            value={formData.singlemengecost}
            onChange={handleInputChange}
          />
          {/* Field 10: auftragswertNetto */}
          <InputField
            label="Auftragswert netto"
            name="auftragswertNetto"
            value={formData.auftragswertNetto}
            onChange={handleInputChange}
          />
          {/* Field 10: Maximaler Lohnaufwand with Unit Selection */}
          <div className="col-span-2">
            <label className="block text-sm font-medium ">
              Maximaler Lohnaufwand
            </label>
            <div className="flex space-x-4">
              <InputField
                name="maxLohnaufwand"
                value={formData.maxLohnaufwand}
                onChange={handleInputChange}
                type="number"
                placeholder="Wert in Zahlen"
              />
              <select
                name="maxLohnaufwandUnit"
                value={maxLohnaufwandUnit}
                onChange={(e) => setMaxLohnaufwandUnit(e.target.value)}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="hours">Stunden</option>
                <option value="minutes">Minuten</option>
              </select>
            </div>
          </div>
          <div className="col-span-2">
            {/* shippingMethod */}
            <label className="block text-sm font-medium ">Versandart</label>
            <div className="flex space-x-4">
              <select
                name="Versandart"
                value={shippingMethod}
                onChange={(e) => setShippingMethod(e.target.value)}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="" disabled hidden>
                  Versandart wählen
                </option>
                <option value="selbstabholung">Selbstabholung</option>
                <option value="exwabwerk">EXW - ab Werk</option>
                <option value="freihaus">Frei Haus</option>
              </select>
            </div>
          </div>
          <div className="col-span-2">
            {/* packagingType */}
            <label className="block text-sm font-medium ">Verpackungsart</label>
            <div className="flex space-x-4">
              <select
                name="Verpackungsart"
                value={packagingType}
                onChange={(e) => setPackagingType(e.target.value)}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="" disabled hidden>
                  Verpackungsart wählen
                </option>
                <option value="ohne">Ohne</option>
                <option value="einwegpalette">Einwegpalette</option>
                <option value="europalette">Europalette</option>
                <option value="rahmen">Europalette mit Rahmen</option>
                <option value="DHL-kleines">DHL Versand - kleines Paket</option>
                <option value="DHL-großes">DHL Versand - großes Paket</option>
              </select>
            </div>
          </div>
          <div className="col-span-2">
            {/* external Service - Multi Select */}
            <label className="block text-sm font-medium">Fremdleistung</label>
            <div className="flex space-x-4">
              <select
                name="Fremdleistung"
                value=""
                onChange={(e) => {
                  if (
                    e.target.value &&
                    !externalService.includes(e.target.value)
                  ) {
                    setExternalService([...externalService, e.target.value]);
                  }
                }}
                className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
              >
                <option value="" disabled>
                  Fremdleistung hinzufügen
                </option>
                <option value="ohne">Ohne</option>
                <option value="normalisieren">Normalisieren</option>
                <option value="harten">Härten</option>
                <option value="gasnitrieren">Gasnitrieren</option>
                <option value="verzahnen">Verzahnen</option>
                <option value="teiflochbohren">Teiflochbohren</option>
                <option value="fremdleistungen">Fremdleistungen</option>
              </select>
            </div>

            {/* Display selected external services */}
            {externalService.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2">
                {externalService.map((service, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                  >
                    {service}
                    <button
                      type="button"
                      onClick={() => {
                        setExternalService(
                          externalService.filter((_, i) => i !== index),
                        );
                      }}
                      className="ml-2 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="col-span-2">
            <QualityNoteCard
              onQualityDataChange={handleQualityDataChange}
              initialData={{}} // You can pass initial data here
            />
          </div>
          {/* Field 11: Beschreibung */}
          <div className="col-span-2">
            <label className="block text-sm font-medium ">Beschreibung</label>
            <textarea
              name="form_erläuterung"
              value={formData.form_erläuterung}
              onChange={handleInputChange}
              rows={3}
              className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
            />
          </div>
        </div>
        <button
          type="submit"
          className="w-full py-3 px-4 border border-transparent rounded-none shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700 transition duration-150 ease-in-out"
        >
          Auftrag erstellen
        </button>
      </form>
    );
  },
);

// -----------------------------
// Memoized ANRDisplay Component
// -----------------------------
const ANRDisplay = React.memo(
  ({
    anrParts,
    handleIncrementFirst,
    handleDecrementFirst,
    handleIncrementSecond,
    handleDecrementSecond,
    handleIncrementThird,
    handleDecrementThird,
    copyANR,
    color,
    printPostDocumentsBarcode, // Add these props
    clearForm,
  }) => (
    <Card
      elevation={3}
      sx={{
        bgcolor: color,
        transition: "all 0.3s",
      }}
    >
      <div className="p-6">
        <div className="flex items-center space-x-2 mb-6">
          <div className="flex items-center">
            <button
              onClick={handleDecrementFirst}
              className="p-2 hover:bg-blue-100 rounded-lg mr-1"
            >
              <MinusCircle className="w-6 h-6 text-blue-600" />
            </button>
            <span className="text-3xl font-mono font-bold">
              {anrParts.firstNumber}
            </span>
            <button
              onClick={handleIncrementFirst}
              className="p-2 hover:bg-blue-100 rounded-lg ml-1"
            >
              <PlusCircle className="w-6 h-6 text-blue-600" />
            </button>
          </div>
          <span className="mx-2">-</span>
          <div className="flex items-center">
            <button
              onClick={handleDecrementSecond}
              className="p-2 hover:bg-blue-100 rounded-lg mr-1"
            >
              <MinusCircle className="w-6 h-6 text-blue-600" />
            </button>
            <span className="text-3xl font-mono font-bold">
              {anrParts.secondNumber}
            </span>
            <button
              onClick={handleIncrementSecond}
              className="p-2 hover:bg-blue-100 rounded-lg ml-1"
            >
              <PlusCircle className="w-6 h-6 text-blue-600" />
            </button>
          </div>
          <span className="mx-2">-</span>
          <div className="flex items-center">
            <button
              onClick={handleDecrementThird}
              className="p-2 hover:bg-blue-100 rounded-lg mr-1"
            >
              <MinusCircle className="w-6 h-6 text-blue-600" />
            </button>
            <span className="text-3xl font-mono font-bold">
              {anrParts.thirdNumber}
            </span>
            <button
              onClick={handleIncrementThird}
              className="p-2 hover:bg-blue-100 rounded-lg ml-1"
            >
              <PlusCircle className="w-6 h-6 text-blue-600" />
            </button>
          </div>
          <span className="mx-2">-</span>
          <span className="text-3xl font-mono font-bold text-gray-500">
            {anrParts.year}
          </span>
        </div>

        {/* Updated button row with all three buttons */}
        <div className="flex space-x-3">
          <button
            onClick={copyANR}
            className="inline-flex items-center px-4 py-3 border border-transparent rounded-none shadow-sm text-base font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <Copy className="w-4 h-4 mr-2" />
            ANR kopieren
          </button>

          {/* <button
            onClick={printPostDocumentsBarcode}
            className="inline-flex items-center px-4 py-3 border border-transparent rounded-none shadow-sm text-base font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <Printer className="w-4 h-4 mr-2" />
            Post-documents
          </button> */}

          <button
            onClick={clearForm}
            className="inline-flex items-center px-4 py-3 border border-transparent rounded-none shadow-sm text-base font-medium text-white bg-red-600 hover:bg-red-700"
          >
            <X className="w-4 h-4 mr-2" />
            Alles Löschen
          </button>
        </div>
      </div>
    </Card>
  ),
);
// -----------------------------
// Main Component
// -----------------------------
function AddProject() {
  // Form & project state
  const [formData, setFormData] = useState({
    ANR: "",
    MengeCost: "75",
    Menge: "0",
    form_auftragsbezeichnung: "",
    form_baugruppenzeichnung: "",
    form_erläuterung: "",
    Artikelnummer: "",
    singlemengecost: "0",
    maxLohnaufwand: "",
    maxlohnfundUnit: "",
    status: "",
    percentDone: "0",
    invoiceNo: "",
    kundenID: "",
    kostenstelle: "",
    Lieferdatum: "",
    Auftragserlös: "",
    auftragswertNetto: "",
    shippingMethod: "",
    packagingType: "",
    externalService: "",
    qualityNote: {},
  });
  const [projects, setProjects] = useState([]);
  const [editProject, setEditProject] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [error, setError] = useState(null);
  const normalizeExternalService = (value) => {
    if (Array.isArray(value)) {
      return value;
    } else if (typeof value === "string" && value.trim()) {
      try {
        // Try to parse as JSON first (in case it's a stringified array)
        const parsed = JSON.parse(value);
        return Array.isArray(parsed) ? parsed : [value];
      } catch (e) {
        // If parsing fails, treat as single string value
        return [value];
      }
    }
    return [];
  };

  // Pagination state
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  //post documents

  const printPostDocumentsBarcode = useCallback(() => {
    const printWindow = window.open("", "PRINT", "height=800,width=800");
    printWindow.document.write(
      "<html><head><title>Post-dokumentieren Barcode</title>",
    );
    printWindow.document.write(`
      <style>
        @page { size: 8in 4in; margin: 8mm; }
        body { width: 8in; margin: 0; padding: 0; line-height: 1; }
        .barcode { text-align: center; }
        .barcode svg { width: 100% !important; height: 250px !important; }
        .barcode p { font-size: 16px; font-weight: bold; margin: 8px 0; }
      </style>
    `);

    printWindow.document.write("</head><body>");
    const barcodeElement = document.getElementById("barcode-post-documents");
    if (barcodeElement) {
      printWindow.document.write('<div class="barcode">');
      printWindow.document.write(barcodeElement.innerHTML);
      printWindow.document.write("<p>post-dokumentieren</p>");
      printWindow.document.write("</div>");
    } else {
      console.error("Barcode element for post-documents not found.");
    }
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }, []);

  // ANR state
  const [anrParts, setAnrParts] = useState({
    firstNumber: "",
    secondNumber: "",
    thirdNumber: "",
    year: new Date().getFullYear().toString().slice(-2),
  });
  const [latestANR, setLatestANR] = useState(null);
  const [highestANRForYear, setHighestANRForYear] = useState(null);
  const [duplicateProject, setDuplicateProject] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [debounceTimer, setDebounceTimer] = useState(null);

  // Kunden state
  const [kundenList, setKundenList] = useState([]);
  const [lieferantList, setLieferantList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedKunde, setSelectedKunde] = useState(null);
  const [confirmed, setConfirmed] = useState(false);
  const [showKundeModal, setShowKundeModal] = useState(false);

  // Edit customer/supplier state
  const [isEditing, setIsEditing] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editName, setEditName] = useState("");

  // Print dialog state
  const [isPrintDialogOpen, setIsPrintDialogOpen] = useState(false);
  const [selectedPrintFields, setSelectedPrintFields] = useState([]);
  const [selectedProjectForPrint, setSelectedProjectForPrint] = useState(null);

  // New state: unit selection for maximaler Lohnaufwand
  const [maxLohnaufwandUnit, setMaxLohnaufwandUnit] = useState("hours");
  const [shippingMethod, setShippingMethod] = useState("");
  const [packagingType, setPackagingType] = useState("");
  const [externalService, setExternalService] = useState([]);
  const [qualityData, setQualityData] = useState({});

  // Description autocomplete state
  const [descriptionSuggestions, setDescriptionSuggestions] = useState([]);
  const [showDescriptionSuggestions, setShowDescriptionSuggestions] =
    useState(false);
  const [suggestionLoadingTimer, setSuggestionLoadingTimer] = useState(null);

  // ANR Creation Statistics state
  const [creationStats, setCreationStats] = useState({
    isOpen: false,
    parentCreated: 0,
    subCreated: 0,
    totalCreated: 0,
    createdANRs: [],
  });

  const handleQualityDataChange = (data) => {
    setQualityData(data);
  };

  // Fetch description suggestions from API
  const fetchDescriptionSuggestions = useCallback(async (keywords) => {
    if (!keywords || keywords.trim().length === 0) {
      setDescriptionSuggestions([]);
      setShowDescriptionSuggestions(false);
      return;
    }

    try {
      const response = await axios.get(
        `${base_url}/v1/getSuggestedDescriptions`,
        {
          params: { keywords },
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      );

      if (response.data.status === 1 && response.data.data) {
        setDescriptionSuggestions(response.data.data);
        setShowDescriptionSuggestions(response.data.data.length > 0);
      } else {
        setDescriptionSuggestions([]);
        setShowDescriptionSuggestions(false);
      }
    } catch (error) {
      console.error("Error fetching description suggestions:", error);
      setDescriptionSuggestions([]);
      setShowDescriptionSuggestions(false);
    }
  }, []);

  // Handle description input change with debouncing
  const handleDescriptionChange = useCallback(
    (e) => {
      const { value } = e.target;
      setFormData((prev) => ({ ...prev, form_auftragsbezeichnung: value }));

      // Clear previous timer
      if (suggestionLoadingTimer) {
        clearTimeout(suggestionLoadingTimer);
      }

      // Set new debounce timer
      const timer = setTimeout(() => {
        fetchDescriptionSuggestions(value);
      }, 300); // Wait 300ms before fetching suggestions

      setSuggestionLoadingTimer(timer);
    },
    [suggestionLoadingTimer, fetchDescriptionSuggestions],
  );

  // Handle selecting a suggestion
  const handleSelectSuggestion = useCallback((suggestion) => {
    setFormData((prev) => ({ ...prev, form_auftragsbezeichnung: suggestion }));
    setShowDescriptionSuggestions(false);
    setDescriptionSuggestions([]);
  }, []);

  // ANR Creation Statistics Functions
  const showCreationStatsDialog = (parentCreated, subCreated, createdANRs) => {
    if (parentCreated > 0 || subCreated > 0) {
      setCreationStats({
        isOpen: true,
        parentCreated,
        subCreated,
        totalCreated: parentCreated + subCreated,
        createdANRs,
      });
    }
  };

  const closeCreationStatsDialog = () => {
    setCreationStats((prev) => ({ ...prev, isOpen: false }));
  };

  const clearForm = useCallback(() => {
    setFormData({
      MengeCost: "0",
      Menge: "0",
      form_auftragsbezeichnung: "",
      form_erläuterung: "",
      Artikelnummer: "",
      Lieferdatum: "",
      deadline: "",
      completionDate: "",
      kostenstelle: "",
      Auftragserlös: "",
      form_baugruppenzeichnung: "",
      singlemengecost: "",
      auftragswertNetto: "",
      maxLohnaufwand: "",
    });

    // Reset other form-related state
    setSelectedKunde(null);
    setMaxLohnaufwandUnit("hours");
    setShippingMethod("");
    setPackagingType("");
    setExternalService([]);
    setQualityData({});

    toast.success("Formular wurde zurückgesetzt");
  }, []);

  const token = getToken();
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Function to get page heading based on current route
  const getPageHeading = () => {
    const path = location.pathname;
    if (path.includes("/stock/addAuftrage")) {
      return "Lager Erstellen";
    } else if (path.includes("/stock/stockAnr")) {
      return "Auftrag und Lager Erstellen";
    } else {
      return "Auftrag Erstellen";
    }
  };

  const availablePrintFields = [
    { key: "ANR", label: "ANR" },
    { key: "Auftragsbezeichnung", label: "Auftragsbezeichnung" },
    { key: "Menge", label: "Menge" },
    { key: "invoiceNo", label: "Rechnungsnummer" },
    { key: "percentDone", label: "Fortschritt (%)" },
    { key: "status", label: "Status" },
    { key: "deadline", label: "Auftragserteilungsdatum" },
    { key: "Lieferdatum", label: "Lieferdatum" },
    { key: "completionDate", label: "Fertigstellungstermin" },
    { key: "kostenstelle", label: "Kostenstelle" },
    { key: "auftragswertNetto", label: "auftragswertNetto" },
    { key: "MaxLohnaufwand", label: "Maximaler Lohnaufwand" },
    { key: "singlemengecost", label: "Einzelmengenkosten" },
    { key: "shippingMethod", label: "Versandart" },
    { key: "packagingType", label: "Verpackungsart" },
    { key: "externalService", label: "Fremdleistung" },
    { key: "qualityNote", label: "Qualitätsnotiz" },
    { key: "Erläuterung", label: "Beschreibung" },
  ];

  // -----------------------------
  // Effects & Data Fetching
  // -----------------------------
  // Function to get highest ANR for current year (optimized)
  const getHighestANRForCurrentYear = useCallback(async () => {
    try {
      const currentYear = new Date().getFullYear().toString().slice(-2);

      // Use getAllProjects with pagination and year filter for better performance
      const response = await axios.get(`${base_url}/v1/getAllProjects`, {
        params: {
          page: 1,
          limit: 50, // Only get recent projects
          sortField: "ANR",
          sortOrder: "desc",
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.data.status === 1) {
        const projects = response.data.data || [];
        const currentYearProjects = projects.filter(
          (project) => project.ANR && project.ANR.endsWith(`-${currentYear}`),
        );

        if (currentYearProjects.length > 0) {
          // Sort projects numerically by ANR (first number is most important)
          const sortedProjects = currentYearProjects.sort((a, b) => {
            const aFirst = parseInt(a.ANR.split("-")[0], 10);
            const bFirst = parseInt(b.ANR.split("-")[0], 10);
            if (aFirst !== bFirst) return bFirst - aFirst; // Descending

            const aSecond = parseInt(a.ANR.split("-")[1], 10);
            const bSecond = parseInt(b.ANR.split("-")[1], 10);
            if (aSecond !== bSecond) return bSecond - aSecond;

            const aThird = parseInt(a.ANR.split("-")[2], 10);
            const bThird = parseInt(b.ANR.split("-")[2], 10);
            return bThird - aThird;
          });

          const highestANR = sortedProjects[0].ANR;
          setHighestANRForYear(highestANR);

          // Generate next ANR suggestion
          const [first, second, third, year] = highestANR.split("-");
          const nextFirst = (parseInt(first, 10) + 1)
            .toString()
            .padStart(4, "0");
          const suggestedANR = `${nextFirst}-00-00-${year}`;

          setAnrParts({
            firstNumber: nextFirst,
            secondNumber: "00",
            thirdNumber: "00",
            year: year,
          });

          setFormData((prev) => ({
            ...prev,
            ANR: suggestedANR,
          }));
        } else {
          // If no projects for current year, start with 0001
          const firstANR = `0001-00-00-${currentYear}`;
          setAnrParts({
            firstNumber: "0001",
            secondNumber: "00",
            thirdNumber: "00",
            year: currentYear,
          });
          setFormData((prev) => ({
            ...prev,
            ANR: firstANR,
          }));
        }
      }
    } catch (error) {
      console.error(
        "Fehler beim Abrufen der höchsten ANR für aktuelles Jahr:",
        error,
      );
      // Fallback to basic increment from latest ANR
      const currentYear = new Date().getFullYear().toString().slice(-2);
      const fallbackANR = `0001-00-00-${currentYear}`;
      setFormData((prev) => ({ ...prev, ANR: fallbackANR }));
    }
  }, [token]);

  // Function to check if ANR already exists and get project details (optimized)
  const checkForDuplicateANR = useCallback(
    async (anrNumber) => {
      console.log(`🔍 Checking for duplicate ANR: ${anrNumber}`);
      try {
        // Use getAllProjects with ANR filter for targeted search
        const response = await axios.get(`${base_url}/v1/getAllProjects`, {
          params: {
            page: 1,
            limit: 10, // Increase limit to catch more potential matches
            anr: anrNumber, // Filter by specific ANR
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log(`📊 API Response status: ${response.data.status}`);
        console.log(`📊 API Response data:`, response.data.data);

        if (response.data.status === 1) {
          const projects = response.data.data || [];
          console.log(`📋 Found ${projects.length} projects to check`);

          const existingProject = projects.find((project) => {
            console.log(`🔎 Comparing: ${project.ANR} === ${anrNumber}`);
            return project.ANR === anrNumber;
          });

          if (existingProject) {
            console.log(`✅ Found duplicate project:`, existingProject);
            setDuplicateProject(existingProject);
            return existingProject;
          } else {
            console.log(`❌ No duplicate found for ANR: ${anrNumber}`);
          }
        }

        // Clear duplicate project if no match found
        setDuplicateProject(null);
        return null;
      } catch (error) {
        console.error("❌ Fehler beim Überprüfen der ANR-Duplikate:", error);
        return null;
      }
    },
    [token],
  );

  // Fetch latest ANR
  useEffect(() => {
    const fetchLatestANR = async () => {
      try {
        const response = await fetch(`${base_url}/v1/searchAnr`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ last: 1 }),
        });
        if (!response.ok)
          throw new Error("Fehler beim Abrufen der neuesten ANR");
        const data = await response.json();
        if (data.Number) {
          const [first, second, third, year] = data.Number.split("-");
          setAnrParts({
            firstNumber: first,
            secondNumber: second,
            thirdNumber: third,
            year: year,
          });
          setFormData((prev) => ({
            ...prev,
            ANR: data.Number,
            form_auftragsbezeichnung: data.AnrText || "",
          }));
          setLatestANR(data.Number);
          setHighestANRForYear(data.Number);
        }
      } catch (error) {
        console.error("Fehler beim Abrufen der neuesten ANR:", error);
        // Fallback: get highest ANR for current year if searchAnr fails
        getHighestANRForCurrentYear();
      }
    };

    // Use searchAnr first (most reliable)
    fetchLatestANR();
  }, [token, getHighestANRForCurrentYear]);

  // Fetch Kunden and Lieferant function
  const fetchKundenAndLieferant = useCallback(async () => {
    try {
      // Fetch Kunden
      const kundenResponse = await fetch(`${base_url}/v1/getKunden`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const kundenData = await kundenResponse.json();
      setKundenList(kundenData.data || []);

      // Fetch Lieferant
      const lieferantResponse = await fetch(`${base_url}/v1/getLieferant`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const lieferantData = await lieferantResponse.json();
      setLieferantList(lieferantData.data || []);
    } catch (error) {
      setError("Fehler beim Laden der Kunden und Lieferanten.");
      console.error("Fehler beim Abrufen der Kunden und Lieferanten:", error);
    }
  }, [token]);

  // Fetch Kunden and Lieferant on component mount
  useEffect(() => {
    fetchKundenAndLieferant();
  }, [fetchKundenAndLieferant]);

  // Fetch projects
  const fetchProjects = useCallback(async () => {
    setLoading(true);
    try {
      // Build query parameters for pagination
      const queryParams = new URLSearchParams({
        page: (pagination.pageIndex + 1).toString(),
        limit: pagination.pageSize.toString(),
      });

      const response = await axios.get(
        `${base_url}/v1/getAllProjects?${queryParams}`,
      );

      console.log("Projects API Response:", response.data); // Debug log

      if (response.data.status === 1) {
        const { data, pagination } = response.data;
        const { totalProjects, totalPages } = pagination;

        // Normalize external service data for each project
        const normalizedProjects = (data || []).map((project) => ({
          ...project,
          externalService: normalizeExternalService(project.externalService),
        }));

        setProjects(normalizedProjects);
        setRowCount(totalProjects || 0);
        setTotalPages(totalPages || 0);
      }
    } catch (error) {
      console.error("Fehler beim Abrufen der Projekte:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.pageIndex, pagination.pageSize]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
    };
  }, [debounceTimer]);

  // -----------------------------
  // Helper Functions
  // -----------------------------
  const getExpectedANR = useCallback(() => {
    if (!latestANR) return null;
    const [latestFirst, latestSecond, latestThird, latestYear] =
      latestANR.split("-");
    const expectedFirst = (parseInt(latestFirst, 10) + 1)
      .toString()
      .padStart(4, "0");
    return `${expectedFirst}-${latestSecond}-${latestThird}-${latestYear}`;
  }, [latestANR]);

  const checkANRExists = useCallback(
    (anr) => projects.some((project) => project.ANR === anr),
    [projects],
  );

  // -----------------------------
  // ANR Increment/Decrement Handlers
  // -----------------------------
  const handleIncrementFirst = useCallback(async () => {
    console.log(`🚀 Incrementing first number from ${anrParts.firstNumber}`);

    const newFirst = (parseInt(anrParts.firstNumber, 10) + 1)
      .toString()
      .padStart(4, "0");
    const newANR = `${newFirst}-00-00-${anrParts.year}`;

    console.log(`🔢 New ANR will be: ${newANR}`);

    // Update state
    setAnrParts((prev) => ({
      ...prev,
      firstNumber: newFirst,
      secondNumber: "00",
      thirdNumber: "00",
    }));
    setFormData((prevForm) => ({ ...prevForm, ANR: newANR }));

    console.log(`📞 Calling checkForDuplicateANR with: ${newANR}`);

    // Check for duplicates immediately with the new ANR
    const existingProject = await checkForDuplicateANR(newANR);

    console.log(`📋 Existing project result:`, existingProject);

    if (existingProject) {
      console.log(`🚨 Setting duplicate modal to true`);
      setShowDuplicateModal(true);
    } else {
      console.log(`✅ No duplicate found, modal should not show`);
    }
  }, [anrParts, checkForDuplicateANR]);

  const handleDecrementFirst = useCallback(async () => {
    if (!latestANR) return;
    const [latestFirst] = latestANR.split("-");
    const expectedFirst = parseInt(latestFirst, 10) + 1;
    const currentFirst = parseInt(anrParts.firstNumber, 10);
    if (currentFirst > expectedFirst) {
      const newFirst = (currentFirst - 1).toString().padStart(4, "0");
      const newANR = `${newFirst}-${anrParts.secondNumber}-${anrParts.thirdNumber}-${anrParts.year}`;

      // Update state
      setFormData((prevForm) => ({ ...prevForm, ANR: newANR }));
      setAnrParts((prev) => ({ ...prev, firstNumber: newFirst }));

      // Check for duplicates immediately with the new ANR
      const existingProject = await checkForDuplicateANR(newANR);
      if (existingProject) {
        setShowDuplicateModal(true);
      }
    }
  }, [anrParts, latestANR, checkForDuplicateANR]);

  const handleIncrementSecond = useCallback(async () => {
    const newSecond = (parseInt(anrParts.secondNumber, 10) + 1)
      .toString()
      .padStart(2, "0");
    const newANR = `${anrParts.firstNumber}-${newSecond}-${anrParts.thirdNumber}-${anrParts.year}`;

    // Update state
    setAnrParts((prev) => ({ ...prev, secondNumber: newSecond }));
    setFormData((prevForm) => ({ ...prevForm, ANR: newANR }));

    // Check for duplicates immediately with the new ANR
    const existingProject = await checkForDuplicateANR(newANR);
    if (existingProject) {
      setShowDuplicateModal(true);
    }
  }, [anrParts, checkForDuplicateANR]);

  const handleDecrementSecond = useCallback(async () => {
    const currentSecond = parseInt(anrParts.secondNumber, 10);
    if (currentSecond > 0) {
      const newSecond = (currentSecond - 1).toString().padStart(2, "0");
      const newANR = `${anrParts.firstNumber}-${newSecond}-${anrParts.thirdNumber}-${anrParts.year}`;

      // Update state
      setFormData((prevForm) => ({ ...prevForm, ANR: newANR }));
      setAnrParts((prev) => ({ ...prev, secondNumber: newSecond }));

      // Check for duplicates immediately with the new ANR
      const existingProject = await checkForDuplicateANR(newANR);
      if (existingProject) {
        setShowDuplicateModal(true);
      }
    }
  }, [anrParts, checkForDuplicateANR]);

  const handleIncrementThird = useCallback(async () => {
    const newThird = (parseInt(anrParts.thirdNumber, 10) + 1)
      .toString()
      .padStart(2, "0");
    const newANR = `${anrParts.firstNumber}-${anrParts.secondNumber}-${newThird}-${anrParts.year}`;

    // Update state
    setAnrParts((prev) => ({ ...prev, thirdNumber: newThird }));
    setFormData((prevForm) => ({ ...prevForm, ANR: newANR }));

    // Check for duplicates immediately with the new ANR
    const existingProject = await checkForDuplicateANR(newANR);
    if (existingProject) {
      setShowDuplicateModal(true);
    }
  }, [anrParts, checkForDuplicateANR]);

  const handleDecrementThird = useCallback(async () => {
    const currentThird = parseInt(anrParts.thirdNumber, 10);
    if (currentThird > 0) {
      const newThird = (currentThird - 1).toString().padStart(2, "0");
      const newANR = `${anrParts.firstNumber}-${anrParts.secondNumber}-${newThird}-${anrParts.year}`;

      // Update state
      setFormData((prevForm) => ({ ...prevForm, ANR: newANR }));
      setAnrParts((prev) => ({ ...prev, thirdNumber: newThird }));

      // Check for duplicates immediately with the new ANR
      const existingProject = await checkForDuplicateANR(newANR);
      if (existingProject) {
        setShowDuplicateModal(true);
      }
    }
  }, [anrParts, checkForDuplicateANR]);

  const copyANR = useCallback(() => {
    navigator.clipboard.writeText(formData.ANR);
    toast.success("ANR kopiert!");
  }, [formData.ANR]);

  const navigateToStockAuftraege = useCallback(() => {
    navigate("/stock/addAuftrage");
  }, [navigate]);

  // -----------------------------
  // Kunden Modal Handlers
  // -----------------------------
  const filteredKunden = useMemo(() => {
    if (!searchTerm) return [];

    // Filter Kunden and add type tag
    const filteredKundenData = kundenList
      .filter(
        (kunde) =>
          (kunde.bezeichnung || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (kunde.konto || "").toLowerCase().includes(searchTerm.toLowerCase()),
      )
      .map((kunde) => ({ ...kunde, type: "Kunde" }));

    // Filter Lieferant and add type tag
    const filteredLieferantData = lieferantList
      .filter(
        (lieferant) =>
          (lieferant.bezeichnung || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (lieferant.konto || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      )
      .map((lieferant) => ({ ...lieferant, type: "Lieferant" }));

    // Combine both lists
    return [...filteredKundenData, ...filteredLieferantData];
  }, [kundenList, lieferantList, searchTerm]);

  const handleSelectKunde = useCallback((kunde) => {
    setSelectedKunde(kunde);
    setSearchTerm(kunde.bezeichnung);
    setConfirmed(false);
  }, []);

  const handleClearKunde = useCallback(() => {
    setSelectedKunde(null);
    setSearchTerm("");
    setConfirmed(false);
  }, []);

  const handleConfirmKunde = useCallback(() => {
    setConfirmed(true);
    setFormData((prev) => ({ ...prev, kundenID: selectedKunde._id }));
    setShowKundeModal(false);
  }, [selectedKunde]);

  // Edit customer/supplier handlers
  const handleEditName = useCallback((item) => {
    setEditingItem(item);
    setEditName(item.bezeichnung);
    setIsEditing(true);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditingItem(null);
    setEditName("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingItem || !editName.trim()) return;

    try {
      const endpoint =
        editingItem.type === "Kunde"
          ? `${base_url}/v1/updateKunden/${editingItem._id}`
          : `${base_url}/v1/updateLieferant/${editingItem._id}`;

      const response = await fetch(endpoint, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bezeichnung: editName.trim(),
          kurzbezeichnung: editName.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        // Update the local state
        if (editingItem.type === "Kunde") {
          setKundenList((prev) =>
            prev.map((kunde) =>
              kunde._id === editingItem._id
                ? { ...kunde, bezeichnung: editName.trim() }
                : kunde,
            ),
          );
        } else {
          setLieferantList((prev) =>
            prev.map((lieferant) =>
              lieferant._id === editingItem._id
                ? { ...lieferant, bezeichnung: editName.trim() }
                : lieferant,
            ),
          );
        }

        // Update selected kunde if it's the one being edited
        if (selectedKunde && selectedKunde._id === editingItem._id) {
          setSelectedKunde((prev) => ({
            ...prev,
            bezeichnung: editName.trim(),
          }));
        }

        toast.success(`${editingItem.type} erfolgreich aktualisiert`);
        handleCancelEdit();

        // Refetch the data to ensure consistency
        fetchKundenAndLieferant();
      } else {
        toast.error(`Fehler beim Aktualisieren des ${editingItem.type}`);
      }
    } catch (error) {
      console.error("Fehler beim Aktualisieren:", error);
      toast.error(`Fehler beim Aktualisieren des ${editingItem.type}`);
    }
  }, [editingItem, editName, token, selectedKunde, fetchKundenAndLieferant]);

  // -----------------------------
  // API Functions
  // -----------------------------
  const createAnr = useCallback(
    async (ANR) => {
      try {
        await fetch(`${base_url}/v1/createAnr`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            Number: ANR,
            AnrText: formData.form_auftragsbezeichnung,
          }),
        });
      } catch (error) {
        console.error("Fehler beim Erstellen der ANR:", error);
        throw error;
      }
    },
    [formData.form_auftragsbezeichnung],
  );

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Validate ANR format and parent existence before proceeding
      const validationResult = await validateANRForSave(
        formData.ANR,
        formData,
        token,
      );
      if (!validationResult.valid) {
        return; // Stop submission if ANR validation fails
      }

      // Check for duplicate ANR before proceeding
      const existingProject = await checkForDuplicateANR(formData.ANR);
      if (existingProject) {
        setShowDuplicateModal(true);
        return;
      }

      const expectedANR = getExpectedANR();
      if (expectedANR && formData.ANR !== expectedANR) {
        // Optionally enforce sequential ANR creation
        // return;
      }
      if (checkANRExists(formData.ANR)) {
        toast.error(`ANR ${formData.ANR} existiert bereits!`, {
          autoClose: 3000,
        });
        return;
      }

      if (isNaN(Number(formData.Auftragserlös))) {
        toast.error(
          "Bitte geben Sie einen numerischen Wert für Auftragserlös ein.",
          { autoClose: 3000 },
        );
        return;
      }

      try {
        const customerName = selectedKunde
          ? `${selectedKunde.konto} - ${selectedKunde.bezeichnung}`
          : "";
        await createAnr(formData.ANR);

        const projectFormData = new FormData();
        projectFormData.append("ANR", formData.ANR);
        projectFormData.append("Menge", formData.Menge);
        projectFormData.append(
          "Auftragsbezeichnung",
          formData.form_auftragsbezeichnung,
        );
        projectFormData.append(
          "Baugruppenzeichnung",
          formData.form_baugruppenzeichnung,
        );
        projectFormData.append("Erlauterung", formData.form_erläuterung);
        projectFormData.append("Artikelnummer", formData.Artikelnummer);
        projectFormData.append("MengeCost", formData.MengeCost);
        const rawSingleMengeCost = String(formData.singlemengecost ?? "").trim();
        const normalizedSingleMengeCost = (() => {
          if (!rawSingleMengeCost) return 0;
          // German format (comma = decimal, dots = thousands): "1.234,56" or "127,80"
          // English format (dot = decimal): "127.80" — don't strip the dot
          const str = rawSingleMengeCost.includes(",")
            ? rawSingleMengeCost.replace(/\./g, "").replace(/,/g, ".")
            : rawSingleMengeCost;
          const parsed = parseFloat(str.replace(/[^0-9.-]/g, ""));
          return isFinite(parsed) ? parsed : 0;
        })();
        projectFormData.append(
          "singlemengecost",
          isNaN(normalizedSingleMengeCost)
            ? "0"
            : normalizedSingleMengeCost.toFixed(2),
        );
        projectFormData.append("MaxLohnaufwand", formData.maxLohnaufwand);
        projectFormData.append("maxlohnfundUnit", maxLohnaufwandUnit);
        projectFormData.append("status", formData.status);
        projectFormData.append("percentDone", formData.percentDone);
        projectFormData.append("invoiceNo", formData.invoiceNo);
        projectFormData.append("totalMaterialCost", 0);
        projectFormData.append("totalsubcontratorCost", 0);
        projectFormData.append("deadline", formData.deadline);
        projectFormData.append("completionDate", formData.completionDate);
        projectFormData.append("kostenstelle", formData.kostenstelle);
        projectFormData.append("Lieferdatum", formData.Lieferdatum);
        projectFormData.append("Auftragserlös", formData.Auftragserlös);
        projectFormData.append("auftragswertNetto", formData.auftragswertNetto);

        // Add the additional state values
        projectFormData.append("shippingMethod", shippingMethod);
        projectFormData.append("packagingType", packagingType);

        // Handle both Kunde and Lieferant selection
        if (formData.kundenID && formData.kundenID.length === 24) {
          if (selectedKunde?.type === "Lieferant") {
            projectFormData.append("lieferantID", formData.kundenID);
          } else {
            // Default to kundenID for backward compatibility and Kunde type
            projectFormData.append("kundenID", formData.kundenID);
          }
        }

        // Send externalService as individual array items (FormData will handle arrays properly)
        if (externalService && Array.isArray(externalService)) {
          externalService.forEach((service, index) => {
            projectFormData.append(`externalService[${index}]`, service);
          });
        }

        // Alternative approach - send as JSON string but ensure it's properly parsed on backend
        // projectFormData.append("externalService", JSON.stringify(externalService));

        // Handle quality data separately to extract signature
        const qualityDataCopy = { ...qualityData };
        let signatureFile = null;

        if (
          qualityData.signature &&
          qualityData.signature.startsWith("data:image/png;base64,")
        ) {
          try {
            const base64Data = qualityData.signature.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: "image/png" });

            signatureFile = new File(
              [blob],
              `signature_${formData.ANR}_${Date.now()}.png`,
              {
                type: "image/png",
              },
            );

            delete qualityDataCopy.signature;
          } catch (error) {
            console.error("Error converting signature to file:", error);
          }
        }

        projectFormData.append("qualityNote", JSON.stringify(qualityDataCopy));

        if (signatureFile) {
          projectFormData.append("signature", signatureFile);
        }

        const response = await fetch(`${base_url}/v1/createProject`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: projectFormData,
        });

        const result = await response.json();
        if (result.status === 1) {
          toast.success("Auftrag erfolgreich erstellt");

          await fetch(`${base_url}/v1/AddToSheet`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              MengeCost: formData.MengeCost,
              ANR: formData.ANR,
              Auftragsbezeichnung: formData.form_auftragsbezeichnung,
              Menge: formData.Menge,
              Kunde: customerName, // This field name can stay as "Kunde" for the sheet since it's just a display name
              deadline: formData.deadline,
              completionDate: formData.completionDate,
              kostenstelle: formData.kostenstelle,
              Lieferdatum: formData.Lieferdatum,
              Auftragserlös: formData.Auftragserlös,
              Artikelnummer: formData.Artikelnummer,
              Baugruppenzeichnung: formData.form_baugruppenzeichnung,
              singlemengecost: formData.singlemengecost,
              auftragswertNetto: formData.auftragswertNetto,
              maxLohnaufwand:
                formData.maxLohnaufwand + " " + maxLohnaufwandUnit,
              createdAt: new Date().toISOString(),
            }),
          });
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          fetchProjects();

          // Reset the external service array
          setExternalService([]);

          // Show creation statistics dialog
          showCreationStatsDialog(
            validationResult.stats.parentCreated,
            validationResult.stats.subCreated,
            validationResult.stats.createdANRs,
          );
        } else {
          toast.error("Fehler beim Erstellen des Auftrags: " + result.message);
        }
      } catch (error) {
        console.error("Fehler:", error);
        toast.error("Fehler beim Erstellen des Auftrags");
      }
    },
    [
      formData,
      getExpectedANR,
      checkANRExists,
      createAnr,
      fetchProjects,
      maxLohnaufwandUnit,
      shippingMethod,
      packagingType,
      externalService,
      qualityData,
      selectedKunde,
      checkForDuplicateANR,
    ],
  );

  const handleEdit = useCallback(async () => {
    try {
      // Prepare the data for sending - handle externalService array properly
      const editData = { ...editProject };

      // If externalService exists and is an array, convert it to JSON string
      if (editData.externalService && Array.isArray(editData.externalService)) {
        editData.externalService = JSON.stringify(editData.externalService);
      }

      const response = await fetch(`${base_url}/v1/updateProject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editData),
      });

      const result = await response.json();
      if (result.status === 1) {
        toast.success("Auftrag erfolgreich aktualisiert");
        setShowEditModal(false);
        fetchProjects();
      } else {
        toast.error("Fehler beim Aktualisieren des Auftrags");
      }
    } catch (error) {
      console.error("Fehler beim Aktualisieren des Auftrags:", error);
      toast.error("Fehler beim Aktualisieren des Auftrags");
    }
  }, [editProject, fetchProjects]);

  const handleDelete = useCallback(
    async (id) => {
      if (
        window.confirm(
          "Sind Sie sicher, dass Sie diesen Auftrag löschen möchten?",
        )
      ) {
        try {
          const response = await axios.delete(
            `${base_url}/v1/deleteProject/${id}`,
          );
          if (response.data.success) {
            fetchProjects();
          }
        } catch (error) {
          console.error("Fehler:", error);
          toast.error("Fehler beim Löschen des Auftrags");
        }
      }
    },
    [fetchProjects],
  );

  const printBarcode = useCallback((number, werk) => {
    const printWindow = window.open("", "PRINT", "height=800,width=800");
    printWindow.document.write("<html><head><title>Barcode drucken</title>");
    printWindow.document.write(`
       <style>
        @page { size: 8in 4in; margin: 8mm; }
        body { width: 8in; margin: 0; padding: 0; line-height: 1; }
        .barcode { text-align: center; }
        .barcode svg { width: 100% !important; height: 250px !important; }
        .barcode p { font-size: 16px; font-weight: bold; margin: 8px 0; }
      </style>
  `);
    printWindow.document.write("</head><body>");

    const barcodeElement = document.getElementById("barcode-" + number);
    if (barcodeElement) {
      let barcodeHTML = barcodeElement.innerHTML
        .replace(/width="\d+"/, "") // remove fixed width from SVG
        .replace(/height="\d+"/, ""); // remove fixed height

      printWindow.document.write('<div class="barcode">');
      printWindow.document.write(barcodeHTML);
      printWindow.document.write("<p>" + werk + "</p>");
      printWindow.document.write("</div>");
    } else {
      console.error("Barcode-Element nicht gefunden.");
    }
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  }, []);

  const BarcodeContainer = useCallback(
    () => (
      <div style={{ display: "none" }}>
        {projects.map((project) => (
          <div key={project._id} id={"barcode-" + project.ANR}>
            <Barcode width={1.4} height={30} value={project.ANR} />
            {project.kundenID && (
              <div>
                {project.kundenID.bezeichnung} - {project.kundenID.konto}
              </div>
            )}
          </div>
        ))}
      </div>
    ),
    [projects],
  );

  // -----------------------------
  // Custom Print
  // -----------------------------
  const handleCustomPrint = useCallback(() => {
    if (!selectedProjectForPrint) return;
    const printDate = new Date().toLocaleString();
    const barcodeElement = document.getElementById(
      `barcode-${selectedProjectForPrint.ANR}`,
    );
    const barcodeHTML = barcodeElement ? barcodeElement.innerHTML : "";
    const projectMeta = {
      Gedruckt: printDate,
      Referenz: selectedProjectForPrint.ANR,
      Status: selectedProjectForPrint.status || "N/A",
    };
    const metadataHTML = Object.entries(projectMeta)
      .map(
        ([key, value]) => `
        <div class="metadata-item">
          <span class="metadata-label">${key}:</span>
          <span class="metadata-value">${value}</span>
        </div>
      `,
      )
      .join("");
    const tableRows = selectedPrintFields
      .map((fieldKey) => {
        const field = availablePrintFields.find((f) => f.key === fieldKey);
        let value = selectedProjectForPrint[fieldKey] || "";

        if (fieldKey === "deadline" && value) {
          if (value && value != "undefined") {
            const date = new Date(value);

            value = new Intl.DateTimeFormat("de-DE", {
              weekday: "long",
              year: "numeric",
              month: "long",
              day: "numeric",
            }).format(date);
          } else value = "N/A";
        } else if (fieldKey == "MaxLohnaufwand") {
          const rawValue = parseFloat(
            selectedProjectForPrint["MaxLohnaufwand"],
          );
          if (isNaN(rawValue)) {
            value = "0 Stunden";
          } else {
            const unit =
              selectedProjectForPrint["maxlohnfundUnit"] == "minutes"
                ? "Minuten"
                : "Stunden";
            value = `${rawValue} ${unit}`;
          }
        } else if (fieldKey === "externalService") {
          // Handle both array and string formats for external service
          const normalizedValue = normalizeExternalService(value);
          value =
            normalizedValue.length > 0
              ? normalizedValue.join(", ")
              : "Keine Fremdleistungen";
        } else if (typeof value === "number") {
          value = value.toLocaleString();
        } else if (Array.isArray(value)) {
          value = value.join(", ");
        } else if (fieldKey === "qualityNote") {
          if (value && typeof value === "object") {
            const qualityNote = value;
            let qualityHtml =
              '<div style="font-size: 0.9em; line-height: 1.4;">';

            if (qualityNote.datum) {
              const qualityDate = new Date(qualityNote.datum);
              const formattedDate = new Intl.DateTimeFormat("de-DE", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
              }).format(qualityDate);
              qualityHtml += `<div><strong>Datum:</strong> ${formattedDate}</div>`;
            }

            if (qualityNote.werkstucke) {
              qualityHtml += `<div><strong>Anzahl Werkstücke:</strong> ${qualityNote.werkstucke}</div>`;
            }

            if (qualityNote.untershift) {
              qualityHtml += `<div><strong>Unterschrift:</strong> ✓ Vorhanden</div>`;
              if (
                typeof qualityNote.untershift === "string" &&
                (qualityNote.untershift.startsWith("http") ||
                  qualityNote.untershift.startsWith("data:"))
              ) {
                qualityHtml += `<div style="margin-top: 8px;">
                  <img src="${qualityNote.untershift}" 
                       alt="Unterschrift" 
                       style="max-width: 150px; max-height: 90px; border: 1px solid #ddd; border-radius: 4px;" />
                </div>`;
              }
            } else {
              qualityHtml += `<div><strong>Unterschrift:</strong> ✗ Nicht vorhanden</div>`;
            }

            qualityHtml += "</div>";
            value = qualityHtml;
          } else {
            value = "Keine Qualitätsdaten vorhanden";
          }
        }
        return `
          <tr>
            <th class="field-label">${field.label}</th>
            <td class="field-value">${value}</td>
          </tr>
        `;
      })
      .join("");
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Auftrag ${selectedProjectForPrint.ANR} - Details</title>
          <style>
            @media print {
              @page { size: A4; margin: 15mm; }
              body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .no-print { display: none !important; }
            }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; background: #fff; }
            .watermark { position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-45deg); font-size: 100px; opacity: 0.03; z-index: -1; pointer-events: none; }
            .container { max-width: 100%; margin: 0 auto; padding: 20px; }
            .header { padding: 20px 0; border-bottom: 2px solid #2c3e50; margin-bottom: 30px; text-align: center; }
            .header h1 { margin: 0; color: #2c3e50; font-size: 24px; }
            .metadata-section { background: #f8f9fa; padding: 15px; border-radius: 4px; margin-bottom: 30px; display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 15px; }
            .metadata-item { display: flex; flex-direction: column; gap: 5px; }
            .metadata-label { font-size: 0.8em; color: #666; text-transform: uppercase; }
            .metadata-value { font-weight: 500; }
            .barcode-section { text-align: center; margin: 20px 0; padding: 15px; background: #fff; border: 1px solid #eee; border-radius: 4px; }
            .data-table { width: 100%; border-collapse: collapse; margin: 20px 0; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
            .data-table th, .data-table td { padding: 12px 15px; text-align: left; border: 1px solid #ddd; }
            .field-label { background: #f8f9fa; font-weight: 600; color: #2c3e50; width: 30%; }
            .field-value { color: #333; }
            .footer { position: fixed; bottom: 0; left: 0; right: 0; padding: 10px; background: #f8f9fa; border-top: 1px solid #ddd; font-size: 0.8em; color: #666; text-align: center; }
          </style>
        </head>
        <body>
          <div class="watermark">VERTRAULICH</div>
          <div class="container">
            <header class="header">
              <h1>Auftragsdetails</h1>
            </header>
            <div class="metadata-section">
              ${metadataHTML}
            </div>
            <div class="barcode-section">
              ${barcodeHTML}
            </div>
            <table class="data-table">
              ${tableRows}
            </table>
            <footer class="footer">
              <div>Gedruckt am ${printDate} | Vertrauliches Dokument</div>
            </footer>
          </div>
        </body>
      </html>
    `;
    const printWindow = window.open("", "", "width=842,height=595");
    if (!printWindow) {
      console.error("Fehler beim Öffnen des Druckfensters.");
      return;
    }
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.onafterprint = () => {
          printWindow.close();
          setIsPrintDialogOpen(false);
        };
      }, 1000);
    };
  }, [selectedProjectForPrint, selectedPrintFields, availablePrintFields]);

  // -----------------------------
  // Table Columns Definition
  // -----------------------------
  const columns = useMemo(
    () => [
      {
        accessorKey: "ANR",
        header: "ANR",
        size: 150,
        Cell: ({ row }) => (
          <Link
            to={`/ProjectDetails/${row.original.ANR}`}
            className="text-blue-600 hover:text-blue-800"
          >
            {row.original.ANR}
          </Link>
        ),
      },
      {
        accessorKey: "Auftragsbezeichnung",
        header: "Auftragsbezeichnung",
        size: 200,
      },
      {
        accessorKey: "Menge",
        header: "Menge",
        size: 100,
      },
      {
        accessorKey: "status",
        header: "Status",
        Cell: ({ row }) => (
          <button
            onClick={() => {
              setSelectedProject(row.original);
              setShowStatusModal(true);
            }}
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              row.original.status === "fertig"
                ? "bg-green-100 text-green-800"
                : row.original.status === "ausstehend"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
            }`}
          >
            {row.original.status || "Status festlegen"}
          </button>
        ),
      },
      {
        accessorKey: "actions",
        header: "Aktionen",
        Cell: ({ row }) => (
          <div className="flex space-x-2">
            <button
              onClick={() => {
                setEditProject(row.original);
                setShowEditModal(true);
              }}
              className="p-1 text-blue-600 hover:text-blue-900"
            >
              <Edit className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(row.original._id)}
              className="p-1 text-red-600 hover:text-red-900"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() =>
                printBarcode(row.original.ANR, row.original.Auftragsbezeichnung)
              }
              className="p-1 text-gray-600 hover:text-gray-900"
            >
              <Printer className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setSelectedProjectForPrint(row.original);
                setSelectedPrintFields(
                  availablePrintFields.map((field) => field.key),
                );
                setIsPrintDialogOpen(true);
              }}
              className="p-1 text-green-600 hover:text-green-900"
              title="Benutzerdefinierter Druck"
            >
              <Printer className="w-4 h-4" />
            </button>
          </div>
        ),
      },
    ],
    [availablePrintFields, projects, handleDelete, printBarcode],
  );

  // -----------------------------
  // Modal Render Functions
  // -----------------------------
  const renderKundeModal = () => {
    if (!showKundeModal) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md relative">
          <button
            onClick={() => setShowKundeModal(false)}
            className="absolute top-2 right-2 text-gray-400 hover:text-gray-500"
          >
            <X className="w-6 h-6" />
          </button>
          {!selectedKunde ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Kunden/Lieferanten suchen
              </h2>
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Suche nach Kunden- oder Lieferantenname oder Kontonummer"
                  className="w-full px-4 py-3 border border-gray-300 rounded-none focus:ring-blue-500 focus:border-blue-500"
                />
                {searchTerm && filteredKunden.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-none shadow-lg max-h-60 overflow-auto">
                    {filteredKunden.map((item) => (
                      <div
                        key={`${item.type}-${item._id}`}
                        className="px-4 py-3 hover:bg-gray-100 border-b last:border-b-0"
                      >
                        {isEditing && editingItem?._id === item._id ? (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-blue-500 focus:border-blue-500"
                                placeholder="Name eingeben"
                                autoFocus
                              />
                              <div className="flex items-center space-x-1 ml-2">
                                <button
                                  onClick={handleSaveEdit}
                                  className="p-1 text-green-600 hover:text-green-800"
                                  title="Speichern"
                                >
                                  <Check className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  className="p-1 text-red-600 hover:text-red-800"
                                  title="Abbrechen"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-600">
                                Konto: {item.konto}
                              </span>
                              <span
                                className={`px-2 py-1 font-medium rounded-full ${
                                  item.type === "Kunde"
                                    ? "bg-blue-100 text-blue-800"
                                    : "bg-green-100 text-green-800"
                                }`}
                              >
                                {item.type}
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div
                            onClick={() => handleSelectKunde(item)}
                            className="cursor-pointer"
                          >
                            <div className="flex items-center justify-between">
                              <div className="font-medium text-gray-900 flex-1">
                                {item.bezeichnung}
                              </div>
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditName(item);
                                  }}
                                  className="p-1 text-gray-400 hover:text-blue-600"
                                  title="Name bearbeiten"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <span
                                  className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    item.type === "Kunde"
                                      ? "bg-blue-100 text-blue-800"
                                      : "bg-green-100 text-green-800"
                                  }`}
                                >
                                  {item.type}
                                </span>
                              </div>
                            </div>
                            <div className="text-sm text-gray-600 mt-1">
                              Konto: {item.konto}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Ausgewählter {selectedKunde.type || "Kunde"}
                  </h3>
                  <p className="text-gray-500 mt-1">
                    {confirmed
                      ? `${selectedKunde.type || "Kunde"} bestätigt`
                      : "Bitte bestätigen Sie Ihre Auswahl"}
                  </p>
                </div>
                <button
                  onClick={handleClearKunde}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Zurücksetzen
                </button>
              </div>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                {isEditing && editingItem?._id === selectedKunde._id ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <input
                        type="text"
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded text-base focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Name eingeben"
                        autoFocus
                      />
                      <div className="flex items-center space-x-2 ml-3">
                        <button
                          onClick={handleSaveEdit}
                          className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded"
                          title="Speichern"
                        >
                          <Check className="w-5 h-5" />
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded"
                          title="Abbrechen"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">
                        Konto: {selectedKunde.konto}
                      </span>
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-full ${
                          selectedKunde.type === "Kunde"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                        }`}
                      >
                        {selectedKunde.type || "Kunde"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-lg font-medium text-gray-900">
                        {selectedKunde.bezeichnung}
                      </h4>
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEditName(selectedKunde)}
                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                          title="Name bearbeiten"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            selectedKunde.type === "Kunde"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-green-100 text-green-800"
                          }`}
                        >
                          {selectedKunde.type || "Kunde"}
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-600 mt-2">
                      Konto: {selectedKunde.konto}
                    </p>
                  </div>
                )}
              </div>
              {!confirmed && (
                <button
                  onClick={handleConfirmKunde}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-none hover:bg-blue-700 transition-colors"
                >
                  Auswahl bestätigen
                </button>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderStatusModal = () => {
    if (!showStatusModal) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Status aktualisieren</h3>
            <button
              onClick={() => setShowStatusModal(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                value={selectedProject?.status || ""}
                onChange={(e) =>
                  setSelectedProject({
                    ...selectedProject,
                    status: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-none border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="ausstehend">Ausstehend</option>
                <option value="fertig">Fertig</option>
                <option value="anghalten">Angehalten</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Fortschritt (%)
              </label>
              <input
                type="number"
                value={selectedProject?.percentDone || 0}
                onChange={(e) =>
                  setSelectedProject({
                    ...selectedProject,
                    percentDone: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-none border border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-3 border border-gray-300 rounded-none shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Abbrechen
              </button>
              <button
                onClick={handleEdit}
                className="px-4 py-3 border border-transparent rounded-none shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
              >
                Änderungen speichern
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!showEditModal) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-medium">Auftrag bearbeiten</h3>
            <button
              onClick={() => setShowEditModal(false)}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                ANR
              </label>
              <input
                type="text"
                value={editProject?.ANR || ""}
                disabled
                className="mt-1 block w-full rounded-none border-gray-300 bg-gray-50"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Auftragsbezeichnung
              </label>
              <input
                type="text"
                value={editProject?.Auftragsbezeichnung || ""}
                onChange={(e) =>
                  setEditProject({
                    ...editProject,
                    Auftragsbezeichnung: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Menge
              </label>
              <input
                type="number"
                value={editProject?.Menge || ""}
                onChange={(e) =>
                  setEditProject({
                    ...editProject,
                    Menge: e.target.value,
                  })
                }
                className="mt-1 block w-full rounded-none border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            {/* Add External Service editing */}
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fremdleistung
              </label>
              <div className="flex space-x-4 mb-2">
                <select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      const currentServices = normalizeExternalService(
                        editProject?.externalService || [],
                      );
                      if (!currentServices.includes(e.target.value)) {
                        setEditProject({
                          ...editProject,
                          externalService: [...currentServices, e.target.value],
                        });
                      }
                    }
                  }}
                  className="mt-1 block w-full border border-gray-300 bg-white px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-150 ease-in-out rounded-none"
                >
                  <option value="" disabled>
                    Fremdleistung hinzufügen
                  </option>
                  <option value="ohne">Ohne</option>
                  <option value="normalisieren">Normalisieren</option>
                  <option value="harten">Härten</option>
                  <option value="gasnitrieren">Gasnitrieren</option>
                  <option value="verzahnen">Verzahnen</option>
                  <option value="teiflochbohren">Teiflochbohren</option>
                  <option value="fremdleistungen">Fremdleistungen</option>
                </select>
              </div>

              {/* Display selected external services */}
              {editProject?.externalService &&
                normalizeExternalService(editProject.externalService).length >
                  0 && (
                  <div className="flex flex-wrap gap-2">
                    {normalizeExternalService(editProject.externalService).map(
                      (service, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
                        >
                          {service}
                          <button
                            type="button"
                            onClick={() => {
                              const currentServices = normalizeExternalService(
                                editProject.externalService,
                              );
                              const updatedServices = currentServices.filter(
                                (_, i) => i !== index,
                              );
                              setEditProject({
                                ...editProject,
                                externalService: updatedServices,
                              });
                            }}
                            className="ml-2 text-blue-600 hover:text-blue-800"
                          >
                            ×
                          </button>
                        </span>
                      ),
                    )}
                  </div>
                )}
            </div>
          </div>
          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={() => setShowEditModal(false)}
              className="px-4 py-3 border border-gray-300 rounded-none shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Abbrechen
            </button>
            <button
              onClick={handleEdit}
              className="px-4 py-3 border border-transparent rounded-none shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Änderungen speichern
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPrintDialog = () => {
    if (!isPrintDialogOpen) return null;
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-md">
          <h2 className="text-xl font-bold mb-4">
            Felder zum Drucken auswählen
          </h2>
          <div className="space-y-2">
            {availablePrintFields.map((field) => (
              <label key={field.key} className="flex items-center">
                <input
                  type="checkbox"
                  checked={selectedPrintFields.includes(field.key)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedPrintFields((prev) => [...prev, field.key]);
                    } else {
                      setSelectedPrintFields((prev) =>
                        prev.filter((key) => key !== field.key),
                      );
                    }
                  }}
                  className="mr-2"
                />
                {field.label}
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end space-x-2">
            <button
              onClick={() => setIsPrintDialogOpen(false)}
              className="px-4 py-3 bg-gray-300 rounded-none"
            >
              Abbrechen
            </button>
            <button
              onClick={handleCustomPrint}
              className="px-4 py-3 bg-blue-600 text-white rounded-none"
            >
              Drucken
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderDuplicateModal = () => {
    if (!showDuplicateModal || !duplicateProject) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-red-600">
              ⚠️ Projekt mit dieser ANR bereits erstellt
            </h2>
            <button
              onClick={() => {
                setShowDuplicateModal(false);
                setDuplicateProject(null);
              }}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="mb-4">
            <p className="text-gray-700 mb-4">
              Ein Projekt mit der ANR <strong>{duplicateProject.ANR}</strong>{" "}
              existiert bereits:
            </p>

            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    ANR
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {duplicateProject.ANR}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Auftragsbezeichnung
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {duplicateProject.Auftragsbezeichnung || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Menge
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {duplicateProject.Menge || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Status
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {duplicateProject.status || "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Kunde
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {duplicateProject.kundenID
                      ? `${duplicateProject.kundenID.konto} - ${duplicateProject.kundenID.bezeichnung}`
                      : "N/A"}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Erstellt am
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {duplicateProject.createdAt
                      ? new Date(duplicateProject.createdAt).toLocaleDateString(
                          "de-DE",
                        )
                      : "N/A"}
                  </p>
                </div>
                {duplicateProject.Artikelnummer && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Artikelnummer
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {duplicateProject.Artikelnummer}
                    </p>
                  </div>
                )}
                {duplicateProject.kostenstelle && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Kostenstelle
                    </label>
                    <p className="mt-1 text-sm text-gray-900">
                      {duplicateProject.kostenstelle}
                    </p>
                  </div>
                )}
              </div>

              {duplicateProject.Erlauterung && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Beschreibung
                  </label>
                  <p className="mt-1 text-sm text-gray-900">
                    {duplicateProject.Erlauterung}
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <button
              onClick={() => {
                setShowDuplicateModal(false);
                setDuplicateProject(null);
              }}
              className="px-4 py-3 border border-gray-300 rounded-none shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Schließen
            </button>
            {/* <button
              onClick={() => {
                setShowDuplicateModal(false);
                setDuplicateProject(null);
                // Generate a new ANR suggestion
                getHighestANRForCurrentYear();
              }}
              className="px-4 py-3 border border-transparent rounded-none shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Neue ANR generieren
            </button> */}
            <Link
              to={`/ProjectDetails/${duplicateProject.ANR}`}
              className="px-4 py-3 border border-transparent rounded-none shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
              onClick={() => {
                setShowDuplicateModal(false);
                setDuplicateProject(null);
              }}
            >
              Projekt anzeigen
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // ANR Creation Statistics Modal
  const renderCreationStatsModal = () => {
    if (!creationStats.isOpen) return null;

    return (
      <Dialog
        open={creationStats.isOpen}
        onClose={closeCreationStatsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: "12px",
            minHeight: "400px",
          },
        }}
      >
        <DialogTitle
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            color: "white",
            textAlign: "center",
            padding: "24px",
          }}
        >
          <Typography
            variant="h5"
            component="div"
            style={{ fontWeight: "bold" }}
          >
            🎉 ANR Erstellungsstatistiken
          </Typography>
          <Typography
            variant="subtitle2"
            style={{ opacity: 0.9, marginTop: "8px" }}
          >
            Übersicht der erstellten ANRs
          </Typography>
        </DialogTitle>

        <DialogContent style={{ padding: "24px" }}>
          {/* Statistics Summary Cards */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "16px",
              marginBottom: "24px",
            }}
          >
            {/* Total Created Card */}
            <Paper
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, #4CAF50, #45a049)",
                color: "white",
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="h3"
                style={{ fontWeight: "bold", marginBottom: "8px" }}
              >
                {creationStats.totalCreated}
              </Typography>
              <Typography variant="body1">Gesamt erstellt</Typography>
            </Paper>

            {/* Parent Projects Card */}
            <Paper
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, #2196F3, #1976D2)",
                color: "white",
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="h3"
                style={{ fontWeight: "bold", marginBottom: "8px" }}
              >
                {creationStats.parentCreated}
              </Typography>
              <Typography variant="body1">Hauptprojekte</Typography>
            </Paper>

            {/* Sub Projects Card */}
            <Paper
              style={{
                padding: "16px",
                textAlign: "center",
                background: "linear-gradient(135deg, #9C27B0, #7B1FA2)",
                color: "white",
                borderRadius: "8px",
              }}
            >
              <Typography
                variant="h3"
                style={{ fontWeight: "bold", marginBottom: "8px" }}
              >
                {creationStats.subCreated}
              </Typography>
              <Typography variant="body1">Unterprojekte</Typography>
            </Paper>
          </div>

          {/* Created ANRs List */}
          {creationStats.createdANRs.length > 0 && (
            <div>
              <Typography
                variant="h6"
                style={{
                  marginBottom: "16px",
                  fontWeight: "bold",
                  color: "#333",
                }}
              >
                📋 Erstellte ANRs:
              </Typography>
              <div style={{ maxHeight: "300px", overflow: "auto" }}>
                {creationStats.createdANRs.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "16px",
                      marginBottom: "8px",
                      backgroundColor:
                        item.type === "parent" ? "#e3f2fd" : "#f3e5f5",
                      borderRadius: "8px",
                      border: `2px solid ${
                        item.type === "parent" ? "#2196F3" : "#9C27B0"
                      }`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "16px",
                      }}
                    >
                      <span
                        style={{
                          color: item.type === "parent" ? "#1976d2" : "#7b1fa2",
                          fontWeight: "bold",
                          backgroundColor:
                            item.type === "parent" ? "#e3f2fd" : "#f3e5f5",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.75em",
                          border: `1px solid ${
                            item.type === "parent" ? "#1976d2" : "#7b1fa2"
                          }`,
                        }}
                      >
                        {item.type === "parent" ? "HAUPT" : "SUB"}
                      </span>
                      <Typography
                        variant="body1"
                        style={{ fontWeight: "bold" }}
                      >
                        {item.anr}
                      </Typography>
                    </div>
                    <Typography variant="body2" style={{ color: "#666" }}>
                      {item.timestamp.toLocaleTimeString()}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "24px", justifyContent: "center" }}>
          <Button
            onClick={closeCreationStatsDialog}
            variant="contained"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              padding: "8px 32px",
              borderRadius: "8px",
              fontWeight: "bold",
            }}
          >
            Verstanden
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // -----------------------------
  // Main Render
  // -----------------------------
  return (
    <Box m="20px">
      <div className="min-h-screen py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Heading */}
          <div className="mb-8 text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {getPageHeading()}
            </h1>
            <p className="text-gray-600">
              {location.pathname.includes("/stock/addAuftrage")
                ? "Erstellen Sie neue Lager-Einträge"
                : location.pathname.includes("/stock/stockAnr")
                  ? "Erstellen Sie Aufträge und Lager-Einträge gleichzeitig"
                  : "Erstellen Sie neue Aufträge für Ihre Projekte"}
            </p>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Side: ANR Display & Form */}
            <div>
              <ANRDisplay
                anrParts={anrParts}
                handleIncrementFirst={handleIncrementFirst}
                handleDecrementFirst={handleDecrementFirst}
                handleIncrementSecond={handleIncrementSecond}
                handleDecrementSecond={handleDecrementSecond}
                handleIncrementThird={handleIncrementThird}
                handleDecrementThird={handleDecrementThird}
                copyANR={copyANR}
                color={colors.primary[400]}
                printPostDocumentsBarcode={printPostDocumentsBarcode}
                clearForm={clearForm}
              />

              {/* New navigation buttons section - same as StockAuftrage */}
              <div className="flex justify-center space-x-4 my-4">
                <button
                  onClick={() => {
                    navigate("/stock/addAuftrage"); // Navigate to regular Auftrage page
                  }}
                  className="flex-1 p-3 bg-blue-600 text-white hover:bg-blue-700 transition-colors rounded font-medium shadow-md"
                >
                  Lager
                </button>
                <button
                  onClick={() => {
                    navigate("/stock/stockAnr"); // Navigate to regular Auftrage page
                  }}
                  className="flex-1 p-3 bg-green-600 text-white hover:bg-green-700 transition-colors rounded font-medium shadow-md"
                >
                  Aufträge + Lager
                </button>
              </div>

              <Card
                elevation={3}
                sx={{ bgcolor: colors.primary[400], transition: "all 0.3s" }}
              >
                <div className="p-6">
                  <ProjectForm
                    formData={formData}
                    setFormData={setFormData}
                    handleSubmit={handleSubmit}
                    selectedKunde={selectedKunde}
                    setShowKundeModal={setShowKundeModal}
                    maxLohnaufwandUnit={maxLohnaufwandUnit}
                    setMaxLohnaufwandUnit={setMaxLohnaufwandUnit}
                    shippingMethod={shippingMethod}
                    setShippingMethod={setShippingMethod}
                    packagingType={packagingType}
                    setPackagingType={setPackagingType}
                    externalService={externalService}
                    setExternalService={setExternalService}
                    handleQualityDataChange={handleQualityDataChange}
                    editMode={false}
                    initialExternalService={[]}
                    highestANRForYear={highestANRForYear}
                    checkForDuplicateANR={checkForDuplicateANR}
                    setShowDuplicateModal={setShowDuplicateModal}
                    setDuplicateProject={setDuplicateProject}
                    duplicateProject={duplicateProject}
                    debounceTimer={debounceTimer}
                    setDebounceTimer={setDebounceTimer}
                    handleDescriptionChange={handleDescriptionChange}
                    descriptionSuggestions={descriptionSuggestions}
                    showDescriptionSuggestions={showDescriptionSuggestions}
                    handleSelectSuggestion={handleSelectSuggestion}
                    setShowDescriptionSuggestions={
                      setShowDescriptionSuggestions
                    }
                  />
                </div>
              </Card>
            </div>

            {/* Right Side: Recently Created Projects */}
            <Card
              elevation={3}
              sx={{ bgcolor: colors.primary[400], transition: "all 0.3s" }}
            >
              <div className="p-6">
                <h2 className="text-xl font-semibold mb-4">
                  Kürzlich erstellte Aufträge
                </h2>
                <div className="space-y-4">
                  {projects.slice(0, 5).map((project) => (
                    <div
                      key={project._id}
                      className="p-4 border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="font-medium">{project.ANR}</h3>
                          <p className="text-sm text-gray-500">
                            {project.Auftragsbezeichnung}
                          </p>
                        </div>
                        <div>
                          <button
                            onClick={() =>
                              printBarcode(
                                project.ANR,
                                project.Auftragsbezeichnung,
                              )
                            }
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Printer className="w-5 h-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          </div>

          {/* Hidden barcode for post documents */}
          <div style={{ display: "none" }}>
            <div id="barcode-post-documents">
              <Barcode width={1.4} height={30} value="post-documents" />
            </div>
          </div>

          {/* Projects Table */}
          <div className="mt-8 rounded-lg shadow-sm">
            <MaterialReactTable
              columns={columns}
              data={projects}
              enableColumnFilters
              enableColumnOrdering
              enablePagination={true}
              enableSorting
              manualPagination={true}
              rowCount={rowCount}
              pageCount={totalPages}
              onPaginationChange={setPagination}
              state={{
                isLoading: loading,
                pagination,
              }}
              initialState={{
                pagination: {
                  pageIndex: 0,
                  pageSize: 25,
                },
              }}
              muiPaginationProps={{
                showRowsPerPage: true,
                showFirstLastPageButtons: true,
                rowsPerPageOptions: [10, 25, 50, 100],
                labelDisplayedRows: ({ from, to, count }) =>
                  `${from}-${to} von ${
                    count !== -1 ? count : `mehr als ${to}`
                  } (Seite ${pagination.pageIndex + 1} von ${totalPages})`,
                labelRowsPerPage: "Zeilen pro Seite:",
              }}
              muiTableBodyRowProps={({ row }) => ({
                className:
                  row.original.status === "fertig" ? "bg-green-50" : "",
              })}
            />
          </div>
        </div>
        {/* Modals */}
        {renderStatusModal()}
        {renderEditModal()}
        {renderKundeModal()}
        {renderPrintDialog()}
        {renderDuplicateModal()}
        {renderCreationStatsModal()}
        {/* Hidden Barcode Container */}
        <BarcodeContainer />
        <ToastContainer />
      </div>
    </Box>
  );
}

export default AddProject;
